import moment from 'moment'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR, OFFER_TYPE_BED_BANK, OFFER_TYPE_VILLA } from 'constants/offer'
import { isBedbankOffer, isLEOffer, isTourOfferType, isTourV2Offer, isVillaOffer } from 'lib/offer/offerTypes'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

export const ENABLED_OFFER_TYPES_RECENTLY_VIEWED = new Set<App.OfferType>([
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  'direct_tour',
  'partner_tour',
  'connection_tour',
  OFFER_TYPE_TOUR,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_VILLA,
])

const messages = defineMessages({
  dayDurationFrom: {
    id: 'ZcZhJq-C1ZTNJda4abeh',
    defaultMessage: '{count, plural, one {# day from} other {# days from}}',
    description: 'Duration label for tour hot lead card showing day count followed by from',
  },
  nightDurationFrom: {
    id: 'GJVs0pEijW6plmR3n6Os',
    defaultMessage: '{count, plural, one {# night from} other {# nights from}}',
    description: 'Duration label for hotel hot lead card showing night count followed by from',
  },
})

const formatMessage = (
  intl: IntlShape,
  descriptor: MessageDescriptor,
  values?: Record<string, number>,
) => intl.formatMessage(descriptor, values)

export function isOfferValidWithRegion(
  offer: App.Offer | App.Tours.TourV2Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary,
) {
  if (isTourV2Offer(offer) || isBedbankOffer(offer) || isVillaOffer(offer)) return true

  const current = moment()
  const visibilitySchedule = offer.visibilitySchedule
  const canBook = !offer.bookByDate || current.isBefore(moment(offer.bookByDate, 'YYYY-MM-DD'))
  const isVisible = !!visibilitySchedule && current.isBefore(moment(visibilitySchedule.end, moment.ISO_8601))
  return canBook && isVisible
}

export function getHotLeadDurationText(duration: number, offerType: App.OfferType, intl: IntlShape) {
  const timeUnit = isTourOfferType(offerType) ? 'day' : 'night'
  return timeUnit === 'day'
    ? formatMessage(intl, messages.dayDurationFrom, { count: duration })
    : formatMessage(intl, messages.nightDurationFrom, { count: duration })
}

export function getTimeLeftUrgencyLabels(
  offer: App.AnyOffer) {
  if (!isLEOffer(offer)) return []

  return offer.urgencyTags.filter(item => item.type === 'left')
}

export function isHotLead(offer: App.HighIntentOffer): offer is App.HotLeadOffer {
  return offer.category === 'sticky_hot_leads'
}

export function isRecentlyViewed(offer: App.HighIntentOffer): offer is App.RecentlyViewedOffer {
  return offer.category === 'recently_viewed'
}

export function shouldDisplayAsRecentlyViewed(offer: App.HighIntentOffer) {
  return isTourOfferType(offer.offerType)
}

// Abandoned carts should display as hot leads to avoid expensive room/occupancy availability checks
export function shouldDisplayAbandonedCartAsHotLead(offer: App.HighIntentOffer): offer is App.AbandonedCartRecommendation {
  return isAbandonedCart(offer)
}

export function isPriceMissing(offer: App.HighIntentOffer): boolean {
  return !!offer.isPriceMissing
}

export function isAbandonedCart(offer: App.HighIntentOffer): offer is App.AbandonedCartRecommendation {
  return offer.category === 'sticky_abandoned_carts'
}
